// Jurisdiction Atlas data — single source of truth for the atlas map, panel,
// ledger and jurisdiction detail pages. `coverage` is an internal field and is never displayed.
// `line` follows the exact pattern: "Entity set-up and ongoing governance in {name}."
// `facts` drives the optional "Key facts" section on detail pages (registry /
// typical timeline / minimum capital) — indicative figures, review before publishing changes.

const g = (name) => `Entity set-up and ongoing governance in ${name}.`;

const f = (registry, timeline, capital) => [
  { label: "Registry", value: registry },
  { label: "Typical timeline", value: timeline },
  { label: "Minimum capital", value: capital },
];

export const JURISDICTIONS = [
  // Gulf
  {
    slug: "uae", name: "UAE", corridor: "Gulf", isoA3: "ARE", coverage: "coordinated", line: g("UAE"),
    facts: f("Department of Economic Development in the chosen emirate", "Typically 1–3 weeks", "No statutory minimum for most mainland companies"),
  },
  {
    slug: "difc", name: "DIFC", corridor: "Gulf", isoA3: "ARE", coverage: "coordinated", line: g("DIFC"), sub: true,
    facts: f("DIFC Registrar of Companies", "Typically 1–2 weeks", "No minimum for most entity types"),
  },
  {
    slug: "adgm", name: "ADGM", corridor: "Gulf", isoA3: "ARE", coverage: "coordinated", line: g("ADGM"), sub: true,
    facts: f("ADGM Registration Authority", "Typically 1–2 weeks", "No minimum for most entity types"),
  },
  {
    slug: "oman", name: "Oman", corridor: "Gulf", isoA3: "OMN", coverage: "coordinated", line: g("Oman"),
    facts: f("Ministry of Commerce, Industry & Investment Promotion", "Typically 2–4 weeks", "No statutory minimum for most LLCs"),
  },
  {
    slug: "bahrain", name: "Bahrain", corridor: "Gulf", isoA3: "BHR", coverage: "coordinated", line: g("Bahrain"),
    facts: f("Ministry of Industry & Commerce (Sijilat)", "Typically 1–2 weeks", "No statutory minimum for most WLLs"),
  },
  {
    slug: "saudi-arabia", name: "Saudi Arabia", corridor: "Gulf", isoA3: "SAU", coverage: "coordinated", line: g("Saudi Arabia"),
    facts: f("Ministry of Commerce", "Typically 2–4 weeks", "No general statutory minimum under the 2023 Companies Law"),
  },

  // East Africa
  {
    slug: "kenya", name: "Kenya", corridor: "East Africa", isoA3: "KEN", coverage: "coordinated", line: g("Kenya"),
    facts: f("Business Registration Service (via eCitizen)", "Typically about 1 week", "No statutory minimum"),
  },
  {
    slug: "tanzania", name: "Tanzania", corridor: "East Africa", isoA3: "TZA", coverage: "coordinated", line: g("Tanzania"),
    facts: f("Business Registrations & Licensing Agency (BRELA)", "Typically 1–2 weeks", "No statutory minimum"),
  },
  {
    slug: "uganda", name: "Uganda", corridor: "East Africa", isoA3: "UGA", coverage: "coordinated", line: g("Uganda"),
    facts: f("Uganda Registration Services Bureau", "Typically about 1 week", "No statutory minimum"),
  },
  {
    slug: "rwanda", name: "Rwanda", corridor: "East Africa", isoA3: "RWA", coverage: "coordinated", line: g("Rwanda"),
    facts: f("Rwanda Development Board", "Often within 1–2 working days", "No statutory minimum"),
  },
  {
    slug: "burundi", name: "Burundi", corridor: "East Africa", isoA3: "BDI", coverage: "coordinated", line: g("Burundi"),
    facts: f("Investment Promotion Authority (API)", "Typically about 1 week", "No statutory minimum"),
  },
  {
    slug: "south-sudan", name: "South Sudan", corridor: "East Africa", isoA3: "SSD", coverage: "coordinated", line: g("South Sudan"),
    facts: f("Ministry of Justice & Constitutional Affairs", "Typically 2–4 weeks", "No statutory minimum"),
  },
  {
    slug: "dr-congo", name: "DR Congo", corridor: "East Africa", isoA3: "COD", coverage: "coordinated", line: g("DR Congo"),
    facts: f("Guichet Unique de Création d'Entreprise", "Typically 1–2 weeks", "No statutory minimum for most SARLs"),
  },
  {
    slug: "somalia", name: "Somalia", corridor: "East Africa", isoA3: "SOM", coverage: "coordinated", line: g("Somalia"),
    facts: f("Ministry of Commerce & Industry", "Typically 2–4 weeks", "No statutory minimum"),
  },

  // West Africa
  {
    slug: "nigeria", name: "Nigeria", corridor: "West Africa", isoA3: "NGA", coverage: "coordinated", line: g("Nigeria"),
    facts: f("Corporate Affairs Commission (CAC)", "Typically 1–2 weeks", "₦100,000 minimum issued share capital for private companies"),
  },
  {
    slug: "ghana", name: "Ghana", corridor: "West Africa", isoA3: "GHA", coverage: "coordinated", line: g("Ghana"),
    facts: f("Office of the Registrar of Companies", "Typically 1–2 weeks", "No statutory minimum"),
  },

  // Southern Africa
  {
    slug: "zambia", name: "Zambia", corridor: "Southern Africa", isoA3: "ZMB", coverage: "coordinated", line: g("Zambia"),
    facts: f("Patents & Companies Registration Agency (PACRA)", "Typically about 1 week", "No statutory minimum for most private companies"),
  },
  {
    slug: "south-africa", name: "South Africa", corridor: "Southern Africa", isoA3: "ZAF", coverage: "coordinated", line: g("South Africa"),
    facts: f("Companies & Intellectual Property Commission (CIPC)", "Typically about 1 week", "No statutory minimum"),
  },
  {
    slug: "botswana", name: "Botswana", corridor: "Southern Africa", isoA3: "BWA", coverage: "coordinated", line: g("Botswana"),
    facts: f("Companies & Intellectual Property Authority (CIPA)", "Typically 1–2 weeks", "No statutory minimum"),
  },
  {
    slug: "mozambique", name: "Mozambique", corridor: "Southern Africa", isoA3: "MOZ", coverage: "coordinated", line: g("Mozambique"),
    facts: f("Balcão de Atendimento Único (BAÚ)", "Typically 2–3 weeks", "No statutory minimum"),
  },

  // South Asia
  {
    slug: "india", name: "India", corridor: "South Asia", isoA3: "IND", coverage: "coordinated", line: g("India"),
    facts: f("Ministry of Corporate Affairs", "Typically 1–2 weeks", "No statutory minimum since 2015"),
  },
];

export const CORRIDORS = ["Gulf", "East Africa", "West Africa", "Southern Africa", "South Asia"];

export const ATLAS_BASE_PATH = "/services/company-incorporation/jurisdiction-atlas";
